import { ActionIcon, Avatar, Box, Button, CopyButton, Group, NavLink, Paper, SimpleGrid, Skeleton, Text, Tooltip, UnstyledButton } from '@mantine/core';
import classes from './SeatingChart.MarketplaceLinks.module.css';
import tevo from '../../img/tevo.png';
import tm from '../../img/tm.png';
import vs from '../../img/vs.png';
import sh from '../../img/sh.png';
import tnet from '../../img/tnet.jpeg';
import ticombo from '../../img/tc.webp';
import SeatGeekLogo from '../../img/sg.svg?react';
import EditIcon from '../../components/icons/Edit';
import ContentCopyIcon from '../../components/icons/ContentCopy';
import OpenNewIcon from '../../components/icons/OpenNew';
import { useSeatingChart } from './SeatingChart.hooks';
import { BrokerNerdsBarkerCoreEnumsMarketplace, BrokerNerdsBarkerCoreModelsInventoryEventMapping, useGetApiMarketplacesMarketplaceIdEventsEventId } from '../../api';
import { generateMarketplaceEventUrl, generateStubHubUrl, generateVividSeatsUrl, parseStubHubUrl, parseVividSeatsUrl } from '../../utils/urls';
import { IconCheck } from '@tabler/icons-react';
import CheckIcon from '../../components/icons/Check';
import { useForm, UseFormReturnType } from '@mantine/form';
import { useEventMapping } from '../EventMapping/EventMapping.hooks';
import SyncAltIcon from '../../components/icons/SyncAlt';
import { useRef, useState } from 'react';
import { BNTextInput } from '../../components/TextInput/TextInput';
import { useClickOutside } from '@mantine/hooks';
import { useFlag } from '@unleash/proxy-client-react';
import BNText from '../../components/Text/BNText';
import { useDelayedValue } from '../../utils/use-delayed-value';

export const supportedMarketplaces = [
  {
    marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster,
    title: 'Ticketmaster',
    img: tm,
    disable: false,
    hasAPI: true,
    url: '',
  },
  {
    marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace.StubHub,
    title: 'StubHub',
    img: sh,
    disable: false,
    hasAPI: false,
    url: '',
  },
  {
    marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace.VividSeats,
    title: 'Vivid Seats',
    img: vs,
    disable: false,
    hasAPI: false,
    url: '',
  },
  {
    marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace.SeatGeek,
    title: 'SeatGeek',
    svg: SeatGeekLogo,
    disable: false,
    hasAPI: true,
    url: '',
  },
  {
    marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace.TicketEvolution,
    title: 'TEVO',
    img: tevo,
    disable: false,
    hasAPI: true,
    url: '',
  },
  {
    marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace.TicketNetwork,
    title: 'TicketNetwork',
    img: tnet,
    disable: false,
    hasAPI: true,
    url: '',
  },
  {
    marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo,
    title: 'Ticombo',
    img: ticombo,
    disable: false,
    hasAPI: true,
    url: '',
  },
];

export function MarketplaceLinks() {
  const { setSelectedMarketplaceId, closeMarketplaceLinks, setIsMarketSelectorOpen, setShowEventMapping, eventMappings } = useSeatingChart(
    'setSelectedMarketplaceId',
    'closeMarketplaceLinks',
    'setIsMarketSelectorOpen',
    'setShowEventMapping',
    'eventMappings',
  );
  const isTicomboMarketplaceEnabled = useFlag('ticombo-marketplace');
  const { linkEvent, selectedEvent, unlinkEvent } = useEventMapping('linkEvent', 'selectedEvent', 'unlinkEvent', 'selectedMarketplaceEvent');
  const toggle = document.querySelector('[data-market-links-toggle]') as HTMLElement;
  const [marketLinksRef, setMarketLinksRef] = useState<HTMLElement | null>(null);
  useClickOutside(closeMarketplaceLinks, null, [marketLinksRef, toggle]);

  const { isFetching: ticomboIsFetchingUrl, url: ticomboUrl } = useMarketplaceUrl(
    selectedEvent?.tenantId ?? '',
    BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo,
    eventMappings,
    isTicomboMarketplaceEnabled,
  );
  const { isFetching: ticketMasterIsFetchingUrl, url: ticketMasterUrl } = useMarketplaceUrl(
    selectedEvent?.tenantId ?? '',
    BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster,
    eventMappings,
  );

  const marketplaces = supportedMarketplaces.map((m) => {
    const mapping = eventMappings?.find((em) => em.marketplaceId === m.marketplaceId);
    const flagDisable = !isTicomboMarketplaceEnabled && m.marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo;

    let url = mapping?.marketplaceEvent?.url ?? '';

    switch (m.marketplaceId) {
      case BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster:
        url = url || ticketMasterUrl;
        break;
      case BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo:
        url = url || ticomboUrl;
        break;
      case BrokerNerdsBarkerCoreEnumsMarketplace.SeatGeek:
      case BrokerNerdsBarkerCoreEnumsMarketplace.TicketEvolution:
      case BrokerNerdsBarkerCoreEnumsMarketplace.TicketNetwork:
        if (mapping?.marketplaceEventId) {
          url = generateMarketplaceEventUrl(m.marketplaceId, mapping.marketplaceEventId);
        }
        break;
    }

    return { ...m, flagDisable, url };
  });

  const stubHubLink = generateStubHubUrl(eventMappings?.find((mapping) => mapping.marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.StubHub)?.marketplaceEventId ?? null);
  const vividSeatsLink = generateVividSeatsUrl(
    eventMappings?.find((mapping) => mapping.marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.VividSeats)?.marketplaceEventId ?? null,
  );
  const stubHubForm = useForm<{ url: string }>({
    initialValues: {
      url: stubHubLink,
    },
    validate: {
      url: (value) => (value === null || value === '' ? null : parseStubHubUrl(value) === null ? 'Invalid URL' : null),
    },
  });
  const vividSeatsForm = useForm<{ url: string }>({
    initialValues: {
      url: vividSeatsLink,
    },
    validate: {
      url: (value) => (value === null || value === '' ? null : parseVividSeatsUrl(value) === null ? 'Invalid URL' : null),
    },
  });

  return (
    <Paper ref={setMarketLinksRef} className={classes.MarketplaceLinks} p={16} shadow="md" pb={10}>
      <Text size="sm" fw={600} w="100%">
        Marketplace Links
      </Text>

      <SimpleGrid
        cols={1}
        my="md"
        mb={0}
        px={0}
        spacing={0}
        // style={{borderBottom: '1px solid var(--colors-gray-0)'}}
        pb={0}
      >
        {marketplaces.map((marketplace, index) => {
          const hasUrl = marketplace.url.length > 0;
          let eventUrl: string;
          if (!hasUrl) {
            switch (marketplace.marketplaceId) {
              case BrokerNerdsBarkerCoreEnumsMarketplace.StubHub:
                eventUrl = stubHubForm.values.url;
                break;
              case BrokerNerdsBarkerCoreEnumsMarketplace.VividSeats:
                eventUrl = vividSeatsForm.values.url;
                break;
              default:
                eventUrl = '';
            }
          } else {
            eventUrl = marketplace.url;
          }

          const skeletonizeLink =
            !marketplace.flagDisable &&
            !marketplace.disable &&
            !marketplace.url &&
            ((ticketMasterIsFetchingUrl && marketplace.marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster) ||
              (ticomboIsFetchingUrl && marketplace.marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo));
          return (
            <Box
              py={10}
              px={5}
              style={{ borderTop: '1px solid var(--colors-gray-0)' }}
              className={classes.MarketplaceItem}
              key={index}
              data-marketplace={marketplace.title}
              {...(marketplace.disable ? { 'data-disable': true } : {})}
            >
              <Group gap={5} className={classes.MarketplaceItemName}>
                {marketplace.svg ? (
                  <Avatar
                    m={0}
                    mt={2}
                    radius="xl"
                    p={0}
                    h={24}
                    w={24}
                    //variant="outline"
                    style={{
                      border: '1px solid var(--colors-gray-2)',
                      minWidth: 0,
                      width: '24px',
                      background: 'transparent',
                    }}
                  >
                    <marketplace.svg />
                  </Avatar>
                ) : (
                  <img
                    className="mantine-Avatar-image"
                    height={24}
                    width={24}
                    src={marketplace.img}
                    alt={marketplace.title}
                    style={{
                      border: '1px solid var(--colors-gray-2)',
                      borderRadius: '30px',
                      background: 'var(--colors-paper)',
                    }}
                  />
                )}

                <Text size="sm" fw={600} fz={12} w="100%">
                  {marketplace.title}
                </Text>
              </Group>

              <Group className={classes.MarketplaceDetails}>
                {marketplace.flagDisable ? (
                  <BNText c="var(--colors-gray-4)">Coming Soon</BNText>
                ) : skeletonizeLink ? (
                  !marketplace.flagDisable && marketplace.url.length <= 0 && marketplace.hasAPI && !marketplace.disable && <Skeleton height={16} />
                ) : (
                  marketplace.url.length <= 0 &&
                  marketplace.hasAPI &&
                  !marketplace.disable && (
                    <UnstyledButton
                      fz="xs"
                      px={5}
                      td="underline"
                      c="var(--colors-gray-4)"
                      onClick={() => {
                        setShowEventMapping(true);
                        setSelectedMarketplaceId(marketplace.marketplaceId);
                        closeMarketplaceLinks();
                      }}
                    >
                      Click to update Event Mapping
                    </UnstyledButton>
                  )
                )}

                {/* ex: TEVO and Ticketmaster */}
                {marketplace.url.length > 0 && marketplace.hasAPI && !marketplace.disable && (
                  <Tooltip // need logic for tooltip, remove it if there is no url, then disable the button, function currently handled via CSS
                    label={`View on ${marketplace.title}`}
                    withArrow
                    position="bottom"
                  >
                    <UnstyledButton fz="xs" td="underline" c="var(--colors-gray-5)" px={5} component="a" href={marketplace.url} target="_blank">
                      {/* strip the domains off*/}
                      {marketplace.url.replace(/^https?:\/\/[^/]+[/]/, './')}
                    </UnstyledButton>
                  </Tooltip>
                )}

                {/* ex: TicketNetwork */}
                {/*{marketplace.disable ? (*/}
                {/*  <Text px={5} c="var(--colors-gray-4)" fw={500} w="100%">*/}
                {/*    Coming Soon*/}
                {/*  </Text>*/}
                {/*) : (*/}
                {/*  <></>*/}
                {/*)}*/}

                {/* ex: Vivid Seats */}
                {/*{marketplace.url.length > 0 && !marketplace.hasAPI && !marketplace.disable ? (*/}
                {/*  <Input // need to work on error states,  can this have url validator?*/}
                {/*    p={0}*/}
                {/*    size="xs"*/}
                {/*    placeholder="Paste an Event URL"*/}
                {/*    value={marketplace.url}*/}
                {/*    rightSection={<CloseButton size={20} />}*/}
                {/*    className={classes.hasClose} // only used for dev to handle the close button appearance*/}
                {/*  />*/}
                {/*) : (*/}
                {/*  <></>*/}
                {/*)}*/}

                {/*/!* ex: StubHub *!/*/}
                {/*{marketplace.url.length <= 0 && !marketplace.hasAPI && !marketplace.disable ? (*/}
                {/*  <Input // need to work on error states,  can this have url validator?*/}
                {/*    size="xs"*/}
                {/*    placeholder="Paste an Event URL"*/}
                {/*    rightSection={<CheckIcon size={16} />}*/}
                {/*    className={classes.hasAccept}*/}
                {/*  />*/}
                {/*) : (*/}
                {/*  <></>*/}
                {/*)}*/}

                {marketplace.marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.StubHub && (
                  <PasteUrlInput
                    onClick={() => {
                      if (stubHubForm.values.url === null || stubHubForm.values.url === '') {
                        unlinkEvent(BrokerNerdsBarkerCoreEnumsMarketplace.StubHub, selectedEvent!.eventId).then(() => {
                          stubHubForm.setValues({ url: generateStubHubUrl(null) });
                          stubHubForm.resetDirty({ url: generateStubHubUrl(null) });
                        });
                      } else {
                        stubHubForm.onSubmit(() => {
                          linkEvent(BrokerNerdsBarkerCoreEnumsMarketplace.StubHub, selectedEvent!.eventId, {
                            marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace.StubHub,
                            marketplaceEventId: parseStubHubUrl(stubHubForm.values.url)!,
                            posEvent: selectedEvent!,
                          }).then(() => {
                            stubHubForm.setValues({
                              url: generateStubHubUrl(parseStubHubUrl(stubHubForm.values.url)!),
                            });
                            stubHubForm.resetDirty({
                              url: generateStubHubUrl(parseStubHubUrl(stubHubForm.values.url)!),
                            });
                          });
                        })();
                      }
                    }}
                    form={stubHubForm}
                  />
                )}

                {marketplace.marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.VividSeats && (
                  <PasteUrlInput
                    onClick={() => {
                      if (vividSeatsForm.values.url === null || vividSeatsForm.values.url === '') {
                        unlinkEvent(BrokerNerdsBarkerCoreEnumsMarketplace.VividSeats, selectedEvent!.eventId).then(() => {
                          vividSeatsForm.setValues({ url: generateVividSeatsUrl(null) });
                          vividSeatsForm.resetDirty({ url: generateVividSeatsUrl(null) });
                        });
                      } else {
                        vividSeatsForm.onSubmit(() => {
                          linkEvent(BrokerNerdsBarkerCoreEnumsMarketplace.VividSeats, selectedEvent!.eventId, {
                            marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace.VividSeats,
                            marketplaceEventId: parseVividSeatsUrl(vividSeatsForm.values.url)!,
                            posEvent: selectedEvent!,
                          }).then(() => {
                            vividSeatsForm.setValues({
                              url: generateVividSeatsUrl(parseVividSeatsUrl(vividSeatsForm.values.url)!),
                            });
                            vividSeatsForm.resetDirty({
                              url: generateVividSeatsUrl(parseVividSeatsUrl(vividSeatsForm.values.url)!),
                            });
                          });
                        })();
                      }
                    }}
                    form={vividSeatsForm}
                  />
                )}
              </Group>

              {!marketplace.disable && (
                <Group
                  gap={5}
                  className={classes.ButtonGroup}
                  // Only apply the disabled look if there is no URL AND we aren't currently skeletonizing
                  {...(marketplace.url.length <= 0 && !skeletonizeLink ? { 'data-disable': true } : {})}
                >
                  {skeletonizeLink ? (
                    /* Single bar representing the 2 icons + View button */
                    <Skeleton height={24} width={130} radius="sm" />
                  ) : (
                    <>
                      <Group gap={1}>
                        <Tooltip label={marketplace.hasAPI ? 'Edit Event Mapping' : 'Edit Event Link'} withArrow position="bottom">
                          <ActionIcon
                            size={24}
                            className={classes.Edit}
                            onClick={() => {
                              setSelectedMarketplaceId(marketplace.marketplaceId);
                              closeMarketplaceLinks();
                              setShowEventMapping(true);
                            }}
                          >
                            <EditIcon color="var(--colors-gray-5)" size={16} />
                          </ActionIcon>
                        </Tooltip>

                        <CopyButton value={eventUrl} timeout={2000}>
                          {({ copied, copy }) => (
                            <Tooltip label={copied ? 'Copied' : 'Copy URL'} withArrow position="bottom">
                              <ActionIcon color={copied ? 'green' : 'gray'} variant="subtle" onClick={copy} size={24} disabled={!eventUrl}>
                                {copied ? <IconCheck size={20} /> : <ContentCopyIcon size={16} />}
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                      </Group>

                      <Tooltip label={`View on ${marketplace.title}`} withArrow position="bottom">
                        <Button
                          component={eventUrl ? 'a' : 'button'}
                          href={eventUrl || undefined}
                          disabled={!eventUrl}
                          target="_blank"
                          color="var(--colors-gray-4)"
                          size="xs"
                          radius="xs"
                          className={classes.View}
                          rightSection={<OpenNewIcon size={16} />}
                        >
                          View
                        </Button>
                      </Tooltip>
                    </>
                  )}
                </Group>
              )}
            </Box>
          );
        })}
      </SimpleGrid>
      <NavLink
        className={classes.link}
        href="#"
        onClick={() => {
          closeMarketplaceLinks();
          setIsMarketSelectorOpen(true);
        }}
        label="Select a Marketplace"
        leftSection={<SyncAltIcon size={20} />}
        pb={0}
        pt={0}
        mt={10}
      />
    </Paper>
  );
}

function PasteUrlInput({ form, onClick }: { form: UseFormReturnType<{ url: string }, { url: string }>; onClick: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <BNTextInput
      size="xs"
      placeholder="Paste an Event URL"
      {...form.getInputProps('url')}
      rightSectionPointerEvents="auto"
      ref={inputRef}
      className={form.isDirty('url') ? classes.hasClose : ''}
      rightSection={
        <ActionIcon
          size={20}
          variant="transparent"
          color="var(--colors-gray-5)"
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          onClick={() => {
            onClick();
            inputRef.current?.blur();
          }}
        >
          <CheckIcon size={16} />
        </ActionIcon>
      }
    />
  );
}

function useMarketplaceUrl(
  tenantId: string,
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  mappings?: BrokerNerdsBarkerCoreModelsInventoryEventMapping[],
  enabled: boolean = true,
) {
  const mapping = mappings?.find((m) => m.marketplaceId === marketplaceId);
  const { data: url, isFetching } = useGetApiMarketplacesMarketplaceIdEventsEventId(marketplaceId, mapping?.marketplaceEventId!, {
    query: {
      enabled: enabled && tenantId !== null && !!mapping?.marketplaceEventId && !mapping?.marketplaceEvent?.url,
      select: (res) => res.data.url,
      staleTime: Infinity,
    },
    axios: {
      headers: {
        'x-tenant-id': tenantId, // Don't use the tenantId from the mapping, mappings can exist for other tenants (i.e. your system tenant)
      },
    },
  });
  const [isFetchingDelayed] = useDelayedValue(isFetching, 300, false);

  return {
    url: mapping?.marketplaceEvent?.url || url || '',
    isFetching: isFetchingDelayed,
  };
}
